import configs from "../../configs.js";
import { getAuthHintCookieName } from "./authCookieNames.js";
import { clearCookie, readCookie, setCookie } from "./cookieUtils.js";

export const AUTH_HINT_TTL_MS = 12 * 60 * 60 * 1000;
export const AUTH_HINT_EVENT = "editor:auth-hint";

function notifyAuthHintChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_HINT_EVENT));
  }
}

function getAuthHintCookieOptions() {
  return {
    domain: configs.AUTH0_COOKIE_DOMAIN
  };
}

function removeHint() {
  try {
    clearCookie(getAuthHintCookieName(), getAuthHintCookieOptions());
    notifyAuthHintChanged();
  } catch (_error) {
    // Ignore cookie access errors.
  }
}

function normalizeDisplayName(user) {
  const name = typeof user?.name === "string" ? user.name.trim() : "";
  if (name) {
    return name;
  }

  const email = typeof user?.email === "string" ? user.email.trim() : "";
  return email || undefined;
}

function normalizePhotoUrl(user) {
  const raw =
    typeof user?.profile_image_url === "string"
      ? user.profile_image_url
      : typeof user?.avatar === "string"
      ? user.avatar
      : "";
  const trimmed = raw.trim();

  if (!trimmed || trimmed.length > 1024) {
    return undefined;
  }

  try {
    const base =
      typeof window !== "undefined" && window.location.origin && window.location.origin !== "null"
        ? window.location.origin
        : "https://example.com";
    const url = new URL(trimmed, base);
    return url.protocol === "https:" || url.protocol === "http:" ? trimmed : undefined;
  } catch (_error) {
    return undefined;
  }
}

function parseAuthHint(raw, now) {
  if (!raw) {
    return null;
  }

  try {
    const hint = JSON.parse(raw);
    if (
      !hint ||
      hint.status !== "maybe_authenticated" ||
      !Number.isFinite(Number(hint.expiresAt)) ||
      Number(hint.expiresAt) <= now
    ) {
      removeHint();
      return null;
    }

    return {
      status: "maybe_authenticated",
      displayName:
        typeof hint.displayName === "string" && hint.displayName.trim() ? hint.displayName.trim() : undefined,
      photoUrl: typeof hint.photoUrl === "string" && hint.photoUrl.trim() ? hint.photoUrl.trim() : undefined,
      expiresAt: Number(hint.expiresAt)
    };
  } catch (_error) {
    removeHint();
    return null;
  }
}

export function readAuthHint(now = Date.now()) {
  return parseAuthHint(readCookie(getAuthHintCookieName()), now);
}

export function writeAuthHint(user, now = Date.now()) {
  if (typeof document === "undefined") {
    return;
  }

  const hint = {
    status: "maybe_authenticated",
    displayName: normalizeDisplayName(user),
    photoUrl: normalizePhotoUrl(user),
    expiresAt: now + AUTH_HINT_TTL_MS
  };

  try {
    setCookie(
      getAuthHintCookieName(),
      JSON.stringify(hint),
      AUTH_HINT_TTL_MS / (24 * 60 * 60 * 1000),
      getAuthHintCookieOptions()
    );
    notifyAuthHintChanged();
  } catch (_error) {
    // Ignore cookie access errors.
  }
}

export function clearAuthHint() {
  removeHint();
}
